import type { AttachmentDto, PageAttachmentDto } from '@/api/attachment';
import { http } from '@/api/http';
import { appendJsonPart } from '@/api/multipart';
import type { OperationResult, Schemas } from '@/api/shared';

/**
 * Families endpoints — list, search, single record, and member mutations.
 *
 * Mirrors the patient-search slice layout (`features/patient-search/api.ts`):
 *   - Hand-written `*Dto` aliases pinned to the generated `Schemas[…]` types
 *   - Operation wrappers that take typed params and return typed bodies
 *   - No DTO → view-model transformation here; mappers live alongside.
 */

export type FamilyDto = Schemas['Family'];
export type FamilySummaryDto = Schemas['FamilySummary'];
export type FamilyMemberRecordDto = Schemas['FamilyMemberRecord'];

/**
 * Subset of `FamilyDto` extended with the fields the live BE returns but the
 * checked-in OpenAPI spec hasn't been regenerated for yet. Remove the cast
 * once `pnpm generate:api` picks up PTV2-1029 / PTV2-1031.
 */
export interface PhysicalChartDto {
  readonly id?: string;
  readonly date?: string | null;
  readonly location?: string | null;
  readonly staff_member_user_guid?: string | null;
  readonly notes?: string | null;
}

export interface FamilyExtensionsDto {
  readonly custom_label?: string | null;
  readonly physical_chart?: PhysicalChartDto | null;
}

/**
 * PATCH body for `/families/{guid}` (PTV2-1029, PTV2-1031). Key *presence*
 * matters: an absent key leaves the field untouched, `null` clears, `""` is
 * an explicit empty string. `physical_chart` is full-block replace — both
 * `location` and `notes` come from the object, missing sub-fields become
 * `null`.
 */
export interface PatchFamilyBody {
  readonly name?: string;
  readonly custom_label?: string | null;
  readonly notes?: string | null;
  readonly physical_chart?: PhysicalChartDto | null;
}

export type CascadeRoleCode = 'PROBAND' | 'FAMILY_MEMBER';
export type ContactStatusCode = 'CONTACTED' | 'NOT_CONTACTED' | 'DECLINED' | 'TESTED';
export type MemberSexCode = string;

/**
 * Body for `POST /families/{guid}/members` (PTV2-1028 FamilyMemberRequestDTO).
 * Dual-mode: pass `person_id` to attach an existing Person, or omit it to
 * create a new INDIVIDUAL person from the demographics in the body.
 * `cascade_role` is required by the BE (400 when absent).
 */
export interface AddFamilyMemberBody {
  readonly cascade_role: CascadeRoleCode;
  readonly person_id?: string | null;
  readonly first_name?: string | null;
  readonly last_name?: string | null;
  readonly date_of_birth?: string | null;
  readonly sex?: MemberSexCode | null;
  readonly deceased?: boolean | null;
  readonly contact_status?: ContactStatusCode | null;
  readonly notes?: string | null;
}

/**
 * Body for `PUT /families/{guid}/members/{memberId}` (FamilyMemberUpdateDTO).
 * Presence-sensitive partial update — only keys present in the body are
 * applied. `contact_status` and `notes` live on the family-member row;
 * `sex`/`deceased`/`date_of_birth` are written through to the person's
 * demographics. `cascade_role` cannot be edited via this endpoint.
 */
export interface UpdateFamilyMemberBody {
  readonly contact_status?: ContactStatusCode;
  readonly notes?: string | null;
  readonly sex?: MemberSexCode | null;
  readonly deceased?: boolean | null;
  readonly date_of_birth?: string | null;
}

/**
 * Spring `Page<FamilyMemberRecord>` envelope returned by
 * `GET /families/{guid}/members`. The checked-in OpenAPI spec hasn't been
 * regenerated to expose this; remove the hand-typed shape once `pnpm
 * generate:api` picks up PTV2-1028.
 */
export interface PageFamilyMemberRecordDto {
  readonly content?: readonly FamilyMemberRecordDto[];
  readonly totalElements?: number;
  readonly totalPages?: number;
  readonly size?: number;
  readonly number?: number;
  readonly first?: boolean;
  readonly last?: boolean;
  readonly numberOfElements?: number;
  readonly empty?: boolean;
}

/**
 * Spring `Page<FamilySummary>` envelope returned by
 * `GET /persons/{guid}/families` (PTV2-1160) and `GET /families/search`. The
 * checked-in OpenAPI spec hasn't been regenerated to expose the reverse-
 * lookup endpoint; drop this hand-typed shape once `pnpm generate:api`
 * catches up.
 */
export interface PageFamilySummaryDto {
  readonly content?: readonly FamilySummaryDto[];
  readonly totalElements?: number;
  readonly totalPages?: number;
  readonly size?: number;
  readonly number?: number;
  readonly first?: boolean;
  readonly last?: boolean;
  readonly numberOfElements?: number;
  readonly empty?: boolean;
}

export interface CreateFamilyBody {
  readonly name: string;
  readonly proband_guid: string;
}

export const familiesApi = {
  /** GET /families?limit=&offset= — paginated family summaries. */
  getFamilies: (
    params: { limit?: number; offset?: number } = {},
    options?: { signal?: AbortSignal },
  ): Promise<OperationResult<'getFamilies'>> =>
    http.get<OperationResult<'getFamilies'>>('/families', {
      ...options,
      query: {
        ...(params.limit !== undefined ? { limit: params.limit } : {}),
        ...(params.offset !== undefined ? { offset: params.offset } : {}),
      },
    }),

  /** POST /families — create a family. Requires `proband_guid` of an existing Person. */
  createFamily: (
    body: CreateFamilyBody,
    options?: { signal?: AbortSignal },
  ): Promise<FamilyDto> => http.post<FamilyDto>('/families', body, options),

  /**
   * POST /families?pedigreeGuid={guid} — create a family and attach an EXISTING
   * pedigree to it (PTV2-1148) rather than minting a fresh one. The body carries
   * only the family `name`; the pedigree to attach is passed as a query param.
   * Returns the created family.
   */
  createFamilyForPedigree: (
    params: { pedigreeGuid: string; name: string },
    options?: { signal?: AbortSignal },
  ): Promise<FamilyDto> =>
    http.post<FamilyDto>(
      '/families',
      { name: params.name },
      { ...options, query: { pedigreeGuid: params.pedigreeGuid } },
    ),

  /**
   * GET /families/search — server-side family search **and** all-families
   * listing. Both `name` and `custom_label` are optional (PTV2-1185); with no
   * criteria the endpoint returns every family in the tenant. `page` / `size`
   * drive the Spring `Page<FamilySummary>` envelope (default 0 / 50, max 200).
   *
   * Returns a flat list. The live BE emits `PageFamilySummaryDTO` with
   * `content[]`; older deployments returned a bare `FamilySummary[]` or
   * `{ data: FamilySummary[] }`. Normalise here so callers never have to
   * chase the shape — keep the three fallbacks until `pnpm generate:api`
   * catches up.
   */
  searchFamilies: async (
    params: { name?: string; custom_label?: string; page?: number; size?: number },
    options?: { signal?: AbortSignal },
  ): Promise<readonly FamilySummaryDto[]> => {
    const response = await http.get<unknown>('/families/search', {
      ...options,
      query: {
        ...(params.name !== undefined && params.name.length > 0 ? { name: params.name } : {}),
        ...(params.custom_label !== undefined && params.custom_label.length > 0
          ? { custom_label: params.custom_label }
          : {}),
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.size !== undefined ? { size: params.size } : {}),
      },
    });
    if (Array.isArray(response)) return response as readonly FamilySummaryDto[];
    if (response !== null && typeof response === 'object') {
      const envelope = response as { data?: unknown; content?: unknown };
      if (Array.isArray(envelope.content)) return envelope.content as readonly FamilySummaryDto[];
      if (Array.isArray(envelope.data)) return envelope.data as readonly FamilySummaryDto[];
    }
    return [];
  },

  /** GET /families/{guid} — full family record incl. members. */
  getFamily: (
    params: { guid: string },
    options?: { signal?: AbortSignal },
  ): Promise<OperationResult<'getFamily'>> =>
    http.get<OperationResult<'getFamily'>>(`/families/${encodeURIComponent(params.guid)}`, options),

  /**
   * GET /families/{guid}/members — paginated family-member records
   * (PTV2-1028). Returns a Spring `Page<FamilyMemberRecord>` envelope.
   */
  getFamilyMembers: (
    params: { guid: string; page?: number; size?: number },
    options?: { signal?: AbortSignal },
  ): Promise<PageFamilyMemberRecordDto> =>
    http.get<PageFamilyMemberRecordDto>(
      `/families/${encodeURIComponent(params.guid)}/members`,
      {
        ...options,
        query: {
          page: params.page ?? 0,
          size: params.size ?? 200,
        },
      },
    ),

  /**
   * GET /persons/{guid}/families — paginated FamilySummary projections of the
   * families the person is a member of (PTV2-1160). Empty page (200) when the
   * person exists but is in no family; 404 when the person guid is unknown.
   */
  listPersonFamilies: (
    params: { guid: string; page?: number; size?: number },
    options?: { signal?: AbortSignal },
  ): Promise<PageFamilySummaryDto> =>
    http.get<PageFamilySummaryDto>(
      `/persons/${encodeURIComponent(params.guid)}/families`,
      {
        ...options,
        query: {
          page: params.page ?? 0,
          size: params.size ?? 50,
        },
      },
    ),

  /**
   * POST /families/{guid}/members — attach an existing person or create a
   * new one + attach. Server returns 201 with the created
   * `FamilyMemberRecord`.
   */
  addFamilyMember: (
    params: { guid: string; body: AddFamilyMemberBody },
    options?: { signal?: AbortSignal },
  ): Promise<FamilyMemberRecordDto> =>
    http.post<FamilyMemberRecordDto>(
      `/families/${encodeURIComponent(params.guid)}/members`,
      params.body,
      options,
    ),

  /**
   * PUT /families/{guid}/members/{memberId} — partial update of a family
   * member's row + write-through demographics. Returns the updated record.
   */
  updateFamilyMember: (
    params: { guid: string; memberId: string; body: UpdateFamilyMemberBody },
    options?: { signal?: AbortSignal },
  ): Promise<FamilyMemberRecordDto> =>
    http.put<FamilyMemberRecordDto>(
      `/families/${encodeURIComponent(params.guid)}/members/${encodeURIComponent(params.memberId)}`,
      params.body,
      options,
    ),

  /** DELETE /families/{guid}/members/{memberId} — remove a member. */
  removeFamilyMember: (
    params: { guid: string; memberId: string },
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/families/${encodeURIComponent(params.guid)}/members/${encodeURIComponent(params.memberId)}`,
      options,
    ),

  /**
   * PATCH /families/{guid} — update name / custom_label / notes / physical_chart.
   * Server returns the updated record (FamilyDto).
   */
  updateFamily: (
    params: { guid: string; body: PatchFamilyBody },
    options?: { signal?: AbortSignal },
  ): Promise<FamilyDto> =>
    http.patch<FamilyDto>(
      `/families/${encodeURIComponent(params.guid)}`,
      params.body,
      options,
    ),

  /**
   * GET /families/{guid}/attachment — paginated family attachments. Returns
   * the same Spring `Page<Attachment>` envelope as the patient route; FE
   * keeps the product-facing "documents" framing.
   */
  listFamilyAttachments: (
    params: { guid: string; pageNumber?: number; pageSize?: number },
    options?: { signal?: AbortSignal },
  ): Promise<PageAttachmentDto> =>
    http.get<PageAttachmentDto>(
      `/families/${encodeURIComponent(params.guid)}/attachment`,
      {
        ...options,
        query: {
          pageNumber: params.pageNumber ?? 0,
          pageSize: params.pageSize ?? 100,
        },
      },
    ),

  /**
   * POST /families/{guid}/attachment — multipart upload (single file).
   * Sends `file` (binary), `type` (enum), `dateObserved` (SimpleDate),
   * and `comments` (string).
   *
   * Every non-file part is encoded as `application/json`: that's what the
   * family controller's `@RequestPart` resolver demands across the board
   * (text/plain and missing content-type both 415). For the string fields
   * the body is a JSON-quoted string (`"IMAGE"`, not `IMAGE`); for
   * `dateObserved` it's the SimpleDate object. The explicit filename on
   * each Blob prevents the browser from dropping the Blob's `type` and
   * defaulting back to `application/octet-stream`.
   */
  uploadFamilyAttachment: (
    params: {
      guid: string;
      file: File;
      type?: string;
      dateObserved?: { year?: number; month?: number; day?: number };
      comments?: string;
    },
    options?: { signal?: AbortSignal },
  ): Promise<AttachmentDto> => {
    const formData = new FormData();
    formData.append('file', params.file);
    if (params.type !== undefined) appendJsonPart(formData, 'type', params.type);
    if (params.dateObserved !== undefined) appendJsonPart(formData, 'dateObserved', params.dateObserved);
    if (params.comments !== undefined) appendJsonPart(formData, 'comments', params.comments);
    return http.post<AttachmentDto>(
      `/families/${encodeURIComponent(params.guid)}/attachment`,
      formData,
      options,
    );
  },

  /** DELETE /families/{guid}/attachment/{attachmentGuid}. */
  deleteFamilyAttachment: (
    params: { guid: string; attachmentGuid: string },
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/families/${encodeURIComponent(params.guid)}/attachment/${encodeURIComponent(params.attachmentGuid)}`,
      options,
    ),
};
